import { TooltipProvider } from "@/components/ui/tooltip";
import {
  ObjectActionMenu,
  useObjectActions,
  type ObjectActionHandlers,
} from "@/object-actions";
import type { Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
  LayoutTemplate,
  MoreHorizontal,
  SquareMousePointer,
  Wand2,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarDropdown,
  CanvasToolbarGroup,
  CanvasToolbarSubmenu,
} from "../components/toolbar";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import {
  isZOrderEligibleForStacking,
  useCanvasZOrderActions,
} from "../hooks/useCanvasZOrderActions";
import { useCopyPaste } from "../hooks/useCopyPaste";
import { runAdoptionForNewSection } from "../hooks/useSectionPlacement";
import {
  computeMultiSelectLayout,
  computeSelectionBounds,
  computeWrapInSection,
  type MultiSelectLayoutMode,
} from "../utils/multiSelectLayout";
import { DEFAULT_SECTION_COLOR } from "./shared/CanvasColorPalettes";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";

const LAYOUT_ITEMS: {
  key: MultiSelectLayoutMode;
  label: string;
  icon: typeof Wand2;
}[] = [
  { key: "align-left", label: "Align left", icon: AlignStartVertical },
  {
    key: "align-h-center",
    label: "Align horizontal centers",
    icon: AlignCenterVertical,
  },
  { key: "align-right", label: "Align right", icon: AlignEndVertical },
  { key: "align-top", label: "Align top", icon: AlignStartHorizontal },
  {
    key: "align-v-center",
    label: "Align vertical centers",
    icon: AlignCenterHorizontal,
  },
  { key: "align-bottom", label: "Align bottom", icon: AlignEndHorizontal },
  { key: "tile", label: "Tile Layout", icon: LayoutTemplate },
];

interface MultiSelectFloatingToolbarProps {
  selectedNodes: Node[];
}

export function MultiSelectFloatingToolbar({
  selectedNodes,
}: MultiSelectFloatingToolbarProps) {
  const { getInternalNode, getNodes, updateNode } = useReactFlow();
  const {
    updateNodePositions,
    addNode,
    batchUpdateNodeParents,
    removeNodes,
    setNodeHidden,
  } = useCanvas();
  const { setToolbarPopoverOpen } = useCanvasUi();
  const { copy, duplicate } = useCopyPaste();
  const { handleNodeBringToFront, handleNodeSendToBack } =
    useCanvasZOrderActions();
  const [layoutOpen, setLayoutOpen] = useState(false);

  const bounds = useMemo(
    () => computeSelectionBounds(selectedNodes, getInternalNode),
    [selectedNodes, getInternalNode],
  );

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      setLayoutOpen(open);
      setToolbarPopoverOpen(open);
    },
    [setToolbarPopoverOpen],
  );

  const applyLayout = useCallback(
    (mode: MultiSelectLayoutMode) => {
      const nodes = getNodes().filter((n) =>
        selectedNodes.some((selected) => selected.id === n.id),
      );
      const updates = computeMultiSelectLayout(nodes, mode, getInternalNode);
      if (updates.length === 0) return;

      updateNodePositions(
        updates.map(({ nodeId, position }) => ({ id: nodeId, position })),
      );
      for (const { nodeId, position } of updates) {
        updateNode(nodeId, { position });
      }
      handlePopoverOpenChange(false);
    },
    [
      selectedNodes,
      getNodes,
      getInternalNode,
      updateNode,
      updateNodePositions,
      handlePopoverOpenChange,
    ],
  );

  const handleWrapInSection = useCallback(() => {
    const layout = computeWrapInSection(selectedNodes, getInternalNode);
    if (!layout) return;

    const sectionId = uuidv4();
    const sectionNode: Node = {
      id: sectionId,
      type: "section",
      position: layout.sectionPosition,
      width: layout.sectionWidth,
      height: layout.sectionHeight,
      zIndex: -1,
      data: {
        title: "Section",
        sectionColor: DEFAULT_SECTION_COLOR,
        noFill: true,
        borderStyle: "solid",
        borderWidth: 2,
      },
    };

    addNode({ ...sectionNode, selected: false });

    batchUpdateNodeParents(
      layout.parentUpdates.map((u) => ({
        ...u,
        parentId: sectionId,
      })),
    );

    runAdoptionForNewSection({
      sectionNode,
      ghost: {
        position: layout.sectionPosition,
        width: layout.sectionWidth,
        height: layout.sectionHeight,
      },
      getNodes,
      getInternalNode,
      batchUpdateNodeParents,
    });
  }, [
    selectedNodes,
    getInternalNode,
    getNodes,
    addNode,
    batchUpdateNodeParents,
  ]);

  const handleDelete = useCallback(() => {
    removeNodes(selectedNodes.map((n) => n.id));
  }, [removeNodes, selectedNodes]);

  // Hide every selected node except sections — sections can't be hidden
  // singly either (their children would stay visible and strand), so the
  // group action matches the single-node menus.
  const handleHide = useCallback(() => {
    for (const node of selectedNodes) {
      if (node.type === "section") continue;
      setNodeHidden(node.id, true);
    }
  }, [selectedNodes, setNodeHidden]);

  const moreMenuHandlers = useMemo<ObjectActionHandlers>(() => {
    const zOrderAnchorId = () =>
      selectedNodes.find(isZOrderEligibleForStacking)?.id;
    return {
      copy,
      duplicate,
      "wrap-in-section": handleWrapInSection,
      "bring-to-front": () => {
        const anchor = zOrderAnchorId();
        if (anchor) handleNodeBringToFront(anchor);
      },
      "send-to-back": () => {
        const anchor = zOrderAnchorId();
        if (anchor) handleNodeSendToBack(anchor);
      },
      "remove-from-canvas": handleHide,
      "bulk-delete": handleDelete,
    };
  }, [
    copy,
    duplicate,
    handleWrapInSection,
    selectedNodes,
    handleNodeBringToFront,
    handleNodeSendToBack,
    handleHide,
    handleDelete,
  ]);

  const { actions: moreMenuActions } = useObjectActions({
    kind: { type: "multi-selection", kinds: [] },
    surface: "context-menu",
    enabled: selectedNodes.length > 1,
    // duplicate is gated on canDuplicateSelection for the workspace list; on the
    // canvas any multi-selection is duplicable, so opt it in explicitly.
    state: { canDuplicateSelection: true },
    handlers: moreMenuHandlers,
  });

  if (!bounds || selectedNodes.length < 2) return null;

  return (
    <CanvasFloatingToolbarPortal
      positionAbsoluteX={bounds.centerX - bounds.width / 2}
      positionAbsoluteY={bounds.topY}
      width={bounds.width}
      topOffset={64}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <TooltipProvider delayDuration={300}>
        <CanvasFloatingToolbarFrame onMouseDown={(e) => e.preventDefault()}>
          <CanvasToolbarSubmenu
            open={layoutOpen}
            onOpenChange={handlePopoverOpenChange}
            side="top"
            trigger={
              <CanvasToolbarButton
                label="Layout"
                dropdown
                active={layoutOpen}
                aria-expanded={layoutOpen}
              >
                <LayoutTemplate className="size-4" />
              </CanvasToolbarButton>
            }
          >
            <CanvasToolbarGroup>
              {LAYOUT_ITEMS.map(({ key, label, icon: Icon }) => (
                <CanvasToolbarButton
                  key={key}
                  label={label}
                  onClick={() => applyLayout(key)}
                >
                  <Icon className="size-4" />
                </CanvasToolbarButton>
              ))}
            </CanvasToolbarGroup>
          </CanvasToolbarSubmenu>

          <CanvasToolbarButton
            label="Tidy up"
            onClick={() => applyLayout("tidy")}
          >
            <Wand2 className="size-4" />
          </CanvasToolbarButton>

          <CanvasToolbarButton
            label="Add to Section"
            onClick={handleWrapInSection}
          >
            <SquareMousePointer className="size-4" />
          </CanvasToolbarButton>

          <CanvasToolbarDivider />

          <CanvasToolbarDropdown
            label="More"
            showCaret={false}
            align="end"
            onOpenChange={setToolbarPopoverOpen}
            icon={<MoreHorizontal className="size-4" />}
          >
            <ObjectActionMenu
              actions={moreMenuActions}
              kind={{ type: "multi-selection", kinds: [] }}
            />
          </CanvasToolbarDropdown>
        </CanvasFloatingToolbarFrame>
      </TooltipProvider>
    </CanvasFloatingToolbarPortal>
  );
}
